import { type Skill } from '@/lib/api/skills.js';

export type SlashSkillOption = Pick<Skill, 'id' | 'title' | 'trigger'> &
  Partial<
    Pick<
      Skill,
      | 'scope'
      | 'pinned_at'
      | 'disabled_at'
      | 'muted_at'
      | 'description'
      | 'last_used_at'
    >
  >;

/**
 * How strongly a scope claims a trigger when two skills share one: private beats workspace beats
 * corporate. Mirrors `scopePrecedence` in cti-conversation-service's `skills.service.ts`, so the
 * skill the slash menu resolves is the one the agent's catalog also picked.
 */
export function scopePrecedence(
  skill: Pick<SlashSkillOption, 'scope'>,
): number {
  if (skill.scope === 'private') return 2;
  if (skill.scope === 'workspace') return 1;
  return 0;
}

/** Pinned skills first (each group in catalog order) — mirrors `SkillsPane`'s partition. */
export function pinnedFirst<T extends SlashSkillOption>(skills: T[]): T[] {
  return [
    ...skills.filter((skill) => skill.pinned_at != null),
    ...skills.filter((skill) => skill.pinned_at == null),
  ];
}

/**
 * Collapses same-trigger duplicates to the most specific scope, matching
 * `resolveByTrigger`'s precedence. Applied before `pinnedFirst` on every
 * list a human picks from (the slash popup, the Enter-to-select shortcut) so
 * a private skill never appears alongside — or loses a position to — a
 * workspace or corporate skill sharing its trigger. Disabled and muted skills
 * are dropped before shadowing (mirroring the server's `catalogFor`): they
 * can't be invoked, and a disabled or muted private skill must not hide a live
 * twin from a wider scope.
 */
export function dedupeByTriggerPrivateFirst<T extends SlashSkillOption>(
  skills: T[],
): T[] {
  const byTrigger = new Map<string, T>();
  for (const skill of skills) {
    if (skill.disabled_at != null || skill.muted_at != null) continue;
    const existing = byTrigger.get(skill.trigger);
    if (!existing || scopePrecedence(skill) > scopePrecedence(existing)) {
      byTrigger.set(skill.trigger, skill);
    }
  }
  return Array.from(byTrigger.values());
}

/** The `/trigger-so-far` typed at position 0 or right after whitespace, immediately before the caret. */
export const SLASH_TOKEN = /(?:^|\s)\/([^\s/]*)$/;

/** How many recently used skills a bare `/` lists. */
const MAX_RECENT_SKILLS = 5;

/**
 * Cyrillic (JCUKEN) letters on the QWERTY letter keys they share, lowercase. The keys are written
 * as unicode escapes to keep the source English-only. Letters sitting on punctuation keys are left
 * out on purpose: mapping them would turn a typed letter into `.` or `;` and match unrelated text.
 */
const RUSSIAN_TO_LATIN_LAYOUT: Record<string, string> = {
  '\u0439': 'q',
  '\u0446': 'w',
  '\u0443': 'e',
  '\u043a': 'r',
  '\u0435': 't',
  '\u043d': 'y',
  '\u0433': 'u',
  '\u0448': 'i',
  '\u0449': 'o',
  '\u0437': 'p',
  '\u0444': 'a',
  '\u044b': 's',
  '\u0432': 'd',
  '\u0430': 'f',
  '\u043f': 'g',
  '\u0440': 'h',
  '\u043e': 'j',
  '\u043b': 'k',
  '\u0434': 'l',
  '\u044f': 'z',
  '\u0447': 'x',
  '\u0441': 'c',
  '\u043c': 'v',
  '\u0438': 'b',
  '\u0442': 'n',
  '\u044c': 'm',
};

/**
 * Re-types text typed on a Russian layout as the latin keys the user meant: lowercased, letters
 * only, so digits, hyphens and latin characters pass through. Typing the keys for `deck` on a Russian layout becomes `deck`.
 */
export function toLatinLayout(text: string): string {
  return Array.from(
    text.toLowerCase(),
    (char) => RUSSIAN_TO_LATIN_LAYOUT[char] ?? char,
  ).join('');
}

export type SlashSectionId = 'pinned' | 'recent' | 'all' | 'matches';

/** One labelled group of the slash menu; the menu renders only non-empty sections. */
export interface SlashMenuSection<T> {
  id: SlashSectionId;
  skills: T[];
}

/** The lowercased query plus its latin-layout form, without the duplicate when they agree. */
function queryForms(query: string): string[] {
  const lowered = query.toLowerCase();
  const latin = toLatinLayout(query);
  return latin === lowered ? [lowered] : [lowered, latin];
}

/**
 * How well a skill matches one needle, best first: 1 trigger prefix, 2 trigger word prefix (split
 * on `-`), 3 trigger substring, 4 description substring. Title and tags are never searched. Null
 * when nothing matches.
 */
function rankAgainstNeedle(
  skill: SlashSkillOption,
  needle: string,
): number | null {
  const trigger = skill.trigger.toLowerCase();
  if (trigger.startsWith(needle)) return 1;
  if (trigger.split('-').some((word) => word.startsWith(needle))) return 2;
  if (trigger.includes(needle)) return 3;
  if (skill.description?.toLowerCase().includes(needle)) return 4;
  return null;
}

/** The best rank over every query form, or null when the skill matches none of them. */
function rankSkill(skill: SlashSkillOption, needles: string[]): number | null {
  const ranks = needles
    .map((needle) => rankAgainstNeedle(skill, needle))
    .filter((rank): rank is number => rank !== null);
  return ranks.length > 0 ? Math.min(...ranks) : null;
}

/** Epoch milliseconds of a skill's last use; skills never used sort as oldest. */
function lastUsedTime(skill: SlashSkillOption): number {
  return skill.last_used_at
    ? Date.parse(skill.last_used_at)
    : Number.NEGATIVE_INFINITY;
}

/**
 * Builds what the slash menu shows. Muted, disabled and shadowed skills are dropped first. A bare
 * query gives `pinned`, `recent` (up to five, newest first, never a pinned skill) and `all`, each
 * skill in exactly one, empty sections omitted. A typed query gives one ranked `matches` section,
 * or none when nothing matches; ties keep pinned skills first, then catalog order.
 */
export function buildSlashSections<T extends SlashSkillOption>(
  skills: T[],
  query: string,
): SlashMenuSection<T>[] {
  const available = dedupeByTriggerPrivateFirst(skills);
  const sections: SlashMenuSection<T>[] = [];
  if (query === '') {
    const pinned = available.filter((skill) => skill.pinned_at != null);
    const unpinned = available.filter((skill) => skill.pinned_at == null);
    const recent = unpinned
      .filter((skill) => skill.last_used_at != null)
      .sort((first, second) => lastUsedTime(second) - lastUsedTime(first))
      .slice(0, MAX_RECENT_SKILLS);
    const rest = unpinned.filter((skill) => !recent.includes(skill));
    const candidates: SlashMenuSection<T>[] = [
      { id: 'pinned', skills: pinned },
      { id: 'recent', skills: recent },
      { id: 'all', skills: rest },
    ];
    return candidates.filter((section) => section.skills.length > 0);
  }
  const needles = queryForms(query);
  const ranked = available
    .map((skill, catalogIndex) => ({
      skill,
      catalogIndex,
      rank: rankSkill(skill, needles),
    }))
    .filter(
      (entry): entry is typeof entry & { rank: number } => entry.rank !== null,
    )
    .sort(
      (first, second) =>
        first.rank - second.rank ||
        Number(first.skill.pinned_at == null) -
          Number(second.skill.pinned_at == null) ||
        first.catalogIndex - second.catalogIndex,
    );
  if (ranked.length > 0) {
    sections.push({
      id: 'matches',
      skills: ranked.map((entry) => entry.skill),
    });
  }
  return sections;
}

/**
 * Where the query sits in `text`, for highlighting: case-insensitive, trying the query as typed and
 * its latin-layout form. Null when it does not occur.
 */
export function findMatchRange(
  text: string,
  query: string,
): [start: number, end: number] | null {
  if (query === '') return null;
  const lowered = text.toLowerCase();
  for (const needle of queryForms(query)) {
    const start = lowered.indexOf(needle);
    if (start >= 0) return [start, start + needle.length];
  }
  return null;
}
